import React, { useEffect } from 'react';

import clsx from 'clsx';
import {
  Paper,
  Drawer,
  Avatar,
  Hidden,
  Button,
  Divider,
  Typography,
} from '@mui/material';
import { isEmpty } from 'lodash';
import PropTypes from 'prop-types';
import { useToggle } from 'react-use';
import useRouter from 'utils/useRouter';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { makeStyles } from '@mui/styles';
import { Link as RouterLink } from 'react-router-dom';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';

import { selectUser, selectLoginDetail } from '@state/selectors';
import { getAvailableRouteLinks } from '@utils/matchRoles';
import useWaitingRoom from '@hooks/useWaitingRoom';
import userAvatar from '@images/default-user.png';
import navigationConfig from './navigationConfig';
import { Navigation } from '@devias-components';
import NavLoginDetail from './NavLoginDetail';

const useStyles = makeStyles((theme) => ({
  root: {
    height: '100%',
    overflowY: 'auto',
  },
  content: {
    padding: theme.spacing(2),
  },
  profile: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    minHeight: 'fit-content',
  },
  avatar: {
    width: 60,
    height: 60,
  },
  name: {
    marginTop: theme.spacing(1),
  },
  divider: {
    marginTop: theme.spacing(2),
  },
  navigation: {
    marginTop: theme.spacing(2),
  },
}));

const NavBar = ({
  openMobile,
  className,
  onMobileClose,
  handleCollapsedNav,
  ...rest
}) => {
  useWaitingRoom();
  const router = useRouter();
  const classes = useStyles();
  const { t } = useTranslation();
  const user = useSelector(selectUser);
  const loginDetail = useSelector(selectLoginDetail);

  const [isOpenNav, toggleNav] = useToggle(true);

  useEffect(() => {
    if (openMobile) {
      onMobileClose && onMobileClose();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.location.pathname]);

  useEffect(() => {
    handleCollapsedNav(isOpenNav);
  }, [isOpenNav, handleCollapsedNav]);

  const navbarContent = (
    <div className={classes.content}>
      <div className={classes.profile}>
        <Avatar
          alt="Person"
          className={classes.avatar}
          component={RouterLink}
          src={userAvatar}
          to="/profile/settings"
        />
        {isOpenNav && (
          <>
            <Typography className={classes.name} variant="h4">
              {user.first_name} {user.last_name}
            </Typography>
            <Typography variant="body2">
              {user.active ? t('active') : t('inactive')}
            </Typography>
            {!isEmpty(loginDetail) && <NavLoginDetail detail={loginDetail} />}
          </>
        )}
      </div>
      <Divider className={classes.divider} />
      <nav className={classes.navigation}>
        {getAvailableRouteLinks(navigationConfig(user.roles, user.permissions))(
          user.roles,
        )(user.permissions).map((list) => (
          <Navigation
            component="div"
            key={list.title}
            pages={list.pages}
            isFullNav={isOpenNav}
            title={t(list.title)}
          />
        ))}
      </nav>
      <Hidden mdDown>
        <Button color="primary" onClick={toggleNav}>
          {isOpenNav ? (
            <>
              <ArrowBackIosIcon /> <Typography>{t('reduce menu')}</Typography>
            </>
          ) : (
            <ArrowForwardIosIcon />
          )}
        </Button>
      </Hidden>
    </div>
  );

  return (
    <>
      <Hidden lgUp>
        <Drawer
          anchor="left"
          open={openMobile}
          variant="temporary"
          onClose={onMobileClose}>
          <div {...rest} className={clsx(classes.root, className)}>
            {navbarContent}
          </div>
        </Drawer>
      </Hidden>
      <Hidden mdDown>
        <Paper
          square
          elevation={1}
          className={clsx(classes.root, className)}
          {...rest}>
          {navbarContent}
        </Paper>
      </Hidden>
    </>
  );
};

NavBar.propTypes = {
  openMobile: PropTypes.bool,
  className: PropTypes.string,
  onMobileClose: PropTypes.func,
  handleCollapsedNav: PropTypes.func.isRequired,
};

export default NavBar;
